import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  CogIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import EnquiryPurposeManager from './EnquiryPurposeManager';

const EnquiryFlowDashboard = ({ leads = [] }) => {
  const [activeView, setActiveView] = useState('overview');
  const [enquiryStats, setEnquiryStats] = useState({});

  useEffect(() => {
    // Calculate enquiry statistics from leads
    const stats = calculateEnquiryStats(leads);
    setEnquiryStats(stats);
  }, [leads]);

  const calculateEnquiryStats = (leads) => {
    const stats = {
      totalEnquiries: leads.length,
      byPurpose: {},
      byStatus: {},
      conversionRate: 0,
      avgValue: 0,
      trending: []
    };

    leads.forEach(lead => {
      // Count by purpose/title
      const purpose = lead.title || 'Other';
      stats.byPurpose[purpose] = (stats.byPurpose[purpose] || 0) + 1;

      // Count by status
      const status = lead.status || 'New';
      stats.byStatus[status] = (stats.byStatus[status] || 0) + 1;
    });

    // Calculate conversion rate (converted leads / total leads)
    const convertedLeads = leads.filter(lead => 
      lead.status === 'Converted' || lead.status === 'Won'
    ).length;
    stats.conversionRate = leads.length > 0 ? (convertedLeads / leads.length * 100).toFixed(1) : 0;

    // Calculate average lead value
    const totalValue = leads.reduce((sum, lead) => sum + (lead.value || 0), 0);
    stats.avgValue = leads.length > 0 ? (totalValue / leads.length).toFixed(0) : 0;

    // Get trending purposes (top 3)
    stats.trending = Object.entries(stats.byPurpose)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([purpose, count]) => ({ purpose, count }));

    return stats;
  };

  const getStatusColor = (status) => {
    const colors = {
      'New': 'bg-blue-100 text-blue-800',
      'Contacted': 'bg-yellow-100 text-yellow-800',
      'Qualified': 'bg-purple-100 text-purple-800',
      'Converted': 'bg-green-100 text-green-800',
      'Lost': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-lg">
        <h1 className="text-3xl font-bold flex items-center">
          <ChartBarIcon className="h-8 w-8 mr-3" />
          Enquiry Purpose Flow Dashboard
        </h1>
        <p className="text-blue-100 mt-2">
          Complete overview of enquiry purposes, conversion tracking, and lead qualification
        </p>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-lg shadow">
        <nav className="flex space-x-8 px-6">
          <button
            onClick={() => setActiveView('overview')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeView === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <ChartBarIcon className="h-4 w-4 inline mr-2" />
            Overview
          </button>
          <button
            onClick={() => setActiveView('analytics')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeView === 'analytics'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <ArrowTrendingUpIcon className="h-4 w-4 inline mr-2" />
            Analytics
          </button>
          <button
            onClick={() => setActiveView('management')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeView === 'management'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <CogIcon className="h-4 w-4 inline mr-2" />
            Management
          </button>
        </nav>
      </div>

      {/* Content */}
      {activeView === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Stats Cards */}
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Enquiries</p>
                  <p className="text-3xl font-bold text-gray-900">{enquiryStats.totalEnquiries}</p>
                </div>
                <UserGroupIcon className="h-8 w-8 text-blue-500" />
              </div>
              <p className="text-sm text-gray-500 mt-2">All time enquiries</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
                  <p className="text-3xl font-bold text-green-600">{enquiryStats.conversionRate}%</p>
                </div>
                <ArrowTrendingUpIcon className="h-8 w-8 text-green-500" />
              </div>
              <p className="text-sm text-gray-500 mt-2">Enquiry to project</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Avg Lead Value</p>
                  <p className="text-3xl font-bold text-purple-600">₹{enquiryStats.avgValue}</p>
                </div>
                <ChartBarIcon className="h-8 w-8 text-purple-500" />
              </div>
              <p className="text-sm text-gray-500 mt-2">Per enquiry</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Purposes</p>
                  <p className="text-3xl font-bold text-indigo-600">{Object.keys(enquiryStats.byPurpose || {}).length}</p>
                </div>
                <ClipboardDocumentListIcon className="h-8 w-8 text-indigo-500" />
              </div>
              <p className="text-sm text-gray-500 mt-2">Service categories</p>
            </div>
          </div>

          {/* Trending Purposes */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Trending Purposes</h3>
            <div className="space-y-3">
              {enquiryStats.trending?.map((item, index) => (
                <div key={item.purpose} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white mr-3 ${
                      index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : 'bg-orange-500'
                    }`}>
                      {index + 1}
                    </span>
                    <span className="text-sm font-medium text-gray-900">{item.purpose}</span>
                  </div>
                  <span className="text-sm font-bold text-gray-600">{item.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeView === 'analytics' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Purpose Breakdown */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Enquiries by Purpose</h3>
            <div className="space-y-3">
              {Object.entries(enquiryStats.byPurpose || {}).map(([purpose, count]) => (
                <div key={purpose} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{purpose}</span>
                  <div className="flex items-center">
                    <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                      <div 
                        className="bg-blue-500 h-2 rounded-full" 
                        style={{ width: `${(count / enquiryStats.totalEnquiries) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Status Breakdown */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Enquiries by Status</h3>
            <div className="space-y-3">
              {Object.entries(enquiryStats.byStatus || {}).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                    {status}
                  </span>
                  <div className="flex items-center">
                    <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                      <div 
                        className="bg-green-500 h-2 rounded-full" 
                        style={{ width: `${(count / enquiryStats.totalEnquiries) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeView === 'management' && (
        <EnquiryPurposeManager />
      )}
    </div>
  );
};

export default EnquiryFlowDashboard;
